<template>
  <div id="MenuComp">
    <!-- Ovelay -->
    <transition name="fade">
      <div v-if="menuModalOpen">
        <button
          :class="overlayClasses"
          @click="closeMenu"
          :title="(closeMenuLabels.btn_title || 'Close menu')"
        />
      </div>
    </transition>
    <!-- Modal -->
    <transition name="fade-r">
      <div
        v-if="menuModalOpen"
        :class="mainClasses"
        role="dialog"
      >
        <div class="relative w-full h-full max-h-full overflow-auto scrolling-touch px-3 md:px-5 flex flex-col text-center">
          <!-- div-top -->
          <div class="w-full h-12 md:h-20 flex justify-end items-center flex-shrink-0">
            <MultiBtn
              v-if="closeMenuLabels"
              :text="closeMenuLabels.btn_text"
              :label="closeMenuLabels.btn_title"
              icon="cross"
              :theme="'button_style_4'"
              @click.native="closeMenu"
            />
          </div>
          <!-- div-middle -->
          <div class="my-auto py-4 flex-shrink-0">
            <!-- Menu -->
            <ol
              v-if="buttonList.length"
              class=""
            >
              <li
                v-for="(linkObj, i1) in buttonList"
                :key="`menu-li-${i1 + 1}`"
                class="mb-6 last:mb-0"
              >
                <!-- Main -->
                <router-link
                  class="text-accent-1 focus:text-black lg:hover:text-accent-2 font-body text-lg font-body-bold focus:outline-none transition-colors duration-500 ease-in-out"
                  :to="linkObj.main.routeTo"
                  :title="htmlDecode(linkObj.main.title)"
                  v-html="linkObj.main.title"
                />
                <!-- Children -->
                <ol
                  v-if="linkObj.children && linkObj.children.length"
                  class="mt-5"
                >
                  <li
                    v-for="(childObj, i2) in linkObj.children"
                    :key="`menu-li-${i1 + 1}-${i2 + 1}`"
                    class="mb-4 last:mb-0"
                  >
                  <!-- Children Link -->
                    <router-link
                      class="text-accent-1 focus:text-black lg:hover:text-accent-2 font-body font-body-normal focus:outline-none transition-colors duration-500 ease-in-out"
                      :to="childObj.routeTo"
                      :title="htmlDecode(childObj.title)"
                      v-html="childObj.title"
                    />
                  </li>
                </ol>
              </li>
            </ol>
            <!-- Linkouts -->
            <CtaList
              olClasses="my-8"
              liClasses="mb-1 last:mb-0"
            />
            <!-- Social media -->
            <ol
              v-if="socialMediaLinks.length"
              class="flex-c"
            >
              <li
                v-for="smLink in socialMediaLinks"
                :key="`social-media-link-${smLink.platform}`"
                class="mr-1 last:mr-0"
              >
                <a
                  :class="[
                    smLinkClasses,
                    `lg:hover:bg-${smLink.platform}`
                  ]"
                  :href="utmHref(smLink.url, 'social-link')"
                  target="_blank"
                  rel="self"
                  :title="smLink.title"
                >
                  <SvgIcon
                    class="w-5 h-5"
                    :icon="smLink.platform"
                  />
                </a>
              </li>
            </ol>
            <LiveEventsPrompt />
          </div>
          <!-- div-bottom -->
          <div class="mb-5 flex-shrink-0 text-center">
            <!-- Legal pages -->
            <p
              v-if="asLabels && asLabels.url && asLabels.btn_text"
              class="my-3 text-sm"
            >
              <a
                class="underline focus:text-black focus:outline-none hover:no-underline"
                :href="utmHref(asLabels.url, 'legal')"
                :title="htmlDecode(asLabels.btn_title || asLabels.btn_text)"
                target="_blank"
                v-html="asLabels.btn_text"
              />
            </p>
            <!-- RV Acknowledgement -->
            <p
              v-if="rvLabels && rvLabels.text && (rvLabels.linkout && rvLabels.linkout.url && rvLabels.linkout.btn_text)"
              class="my-3 text-sm"
            >
              {{ rvLabels.text }}
              <a
                class="underline focus:text-black focus:outline-none hover:no-underline"
                :href="utmHref(rvLabels.linkout.url, 'rv-acknowledgement')"
                :title="htmlDecode(rvLabels.linkout.btn_title || rvLabels.linkout.btn_text)"
                target="_blank"
                v-html="rvLabels.linkout.btn_text"
              />
            </p>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import MultiBtn from '@/components/buttons/MultiBtn.vue'
import CtaList from '@/components/CtaList.vue'
import LiveEventsPrompt from '@/components/LiveEventsPrompt.vue'
import SvgIcon from '@/components/SvgIcon.vue'
import { mapState, mapGetters } from 'vuex'

export default {
  name: 'Menu',

  components: {
    MultiBtn,
    CtaList,
    LiveEventsPrompt,
    SvgIcon
  },

  data: () => ({
    smLinkClasses: 'w-10 h-10 flex-c bg-transparent focus:bg-accent-1 fill-accent-1 focus:fill-white lg:hover:fill-white focus:outline-none transition-colors duration-500 ease-in-out'
  }),

  computed: {
    // Data
    ...mapState([
      'options',
      'menuModalOpen'
    ]),
    ...mapGetters([
      'topLevelAoi',
      'homePage',
      'mainMenuPage',
      'aoiPage',
      'guidedTourPage',
      'justExplorePage',
      'myStore'
    ]),
    MainMenuEnabled() { return this.myStore(['settings', 'main_menu_enabled']) ?? false },
    // Menu
    menuLabels() { return this.myStore(['labels', 'menu']) ?? {} },
    menuStyles() { return this.myStore(['styles', 'menu']) ?? {} },
    // Overlay
    overlayStyles() { return this.menuStyles.overlay ?? {} },
    overlayClasses() {
      const { overlayStyles } = this
      // return
      return [
       'f-cover',
       'overflow-hidden',
       `bg-${overlayStyles.bg_color}`,
       `opacity-${overlayStyles.opacity}`,
       'focus:outline-none'
      ]
    },
    //
    mainStyles() { return this.menuStyles.main ?? {} },
    mainClasses() {
      const { mainStyles } = this
      // return
      return [
        'fixed',
        'right-0 top-0',
        'w-96 max-w-full',
        'h-full',
        'overflow-hidden',
        `bg-${mainStyles.bg_color ?? 'white'}`,
        `text-${mainStyles.text_color ?? 'accent-1'}`,
        `fill-${mainStyles.fill_color ?? 'accent-1'}`,
      ]
    },
    // Close btn
    closeMenuLabels() { return this.menuLabels.close_menu_button ?? {} },
    // Links
    menuLinks() {
      // Default
      const menuLinksArr = [
        {
          text: 'Home',
          title: 'Go to home page',
          routeTo: { name: 'home' }
        }
      ]
      if (this.MainMenuEnabled) {
        menuLinksArr.push({
          text: 'Areas of interest',
          title: 'Go to areas of interest',
          routeTo: { name: 'area-of-interest' }
        })
      } else {
        this.topLevelAoi.forEach((item) => {
          menuLinksArr.push({
            text: item.title,
            title: `Go to ${item.title}`,
            routeTo: {
              name: 'aoi-sublevel',
              params: {
                aoi_id: item.slug
              }
            }
          })
        })
      }
      return menuLinksArr
    },
    buttonList() {
      // Data
      const { topLevelAoi, homePage, mainMenuPage, aoiPage, guidedTourPage, justExplorePage } = this
      const pageList = []
      // Home
      if (homePage) {
        pageList.push({ main: homePage })
      }
      // Main Menu
      const mainMenuEnabled = this.myStore(['settings', 'main_menu_enabled']) ?? false
      if (mainMenuPage && mainMenuEnabled) {
        pageList.push({ main: mainMenuPage })
      }
      // Area of interest
      const areaOfInteresEnabled = this.myStore(['settings', 'areas_of_interest_enabled']) ?? false
      if (aoiPage && areaOfInteresEnabled) {
        const subLevelArr = topLevelAoi.map((item) => {
          item.routeTo = {
            name: 'aoi-sublevel',
            params: { aoi_id: item.slug }
          }
          return item
        })
        pageList.push({
          main: aoiPage,
          children: subLevelArr
        })
      }
      // Guided tout
      const guidedTourEnabled = this.myStore(['settings', 'guided_tour_enabled']) ?? false
      if (guidedTourPage && guidedTourEnabled) {
        pageList.push({ main: guidedTourPage })
      }
      if (justExplorePage) {
        pageList.push({ main: justExplorePage })
      }
      return pageList
    },
    // Social media
    socialMediaLinks() {
      const links = this.myStore(['options', 'social_media_links']) || []
      return links.filter(({ platform, url }) => platform && url)
    },
    // Accessibility Statement (includes deprecated version)
    asLabels() { return this.myStore(['options', 'accessibility_linkout']) || this.menuLabels.accessibility_linkout || false },
    // Acknowledgment
    rvLabels() { return this.menuLabels.rv_acknowledgement || {} },
  },

  watch: {
    menuModalOpen() {
      if (this.menuModalOpen) {
        window.addEventListener('keyup', this.onKeyUp)
      } else {
        window.removeEventListener('keyup', this.onKeyUp)
      }
    }
  },

  methods: {
    onKeyUp({ which }) {
      /* ESC */
      const isEsc = 27 == which
      if (isEsc) { this.closeMenu() }
    },
    closeMenu() {
      this.$store.commit('menuModalOpen', false)
    }
  }
}
</script>
